import {Component, OnInit, HostListener} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {ActivatedRoute, Router} from "@angular/router";
import {HttpStatusCode} from "@angular/common/http";
import {Title} from "@angular/platform-browser";
import {CustomMessageService, LocalStorageService, ResponseHttpError, ResponseHttpSuccess, UtilService} from "library";
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {PathNavigationService} from 'projects/store/src/app/services/api/v1/path-navigation.service';
import { LocalStorageKey, SalesWorkflowSteps, SalesWorkflow, SalesWorkflowRequiresAuthentication } from "projects/store/src/app/enums";
import {WorkflowStep} from "projects/store/src/app/interfaces";
import {environment} from "projects/store/src/environments/environment";
import {AuthService} from "projects/store/src/app/services/api/v1/auth.service";
import {PageNotFoundReasonCode, PageNotFoundReasons} from "projects/store/src/app/enums/page-not-found-reasons.enum";
import {OriginMessageError} from "library";
import { MacroFlowService } from 'projects/store/src/app/services/api/v1/macro-flow.service';

const FLOW_SELLER_NOT_ALLOWED_MSG = 'Se está intentando hacer un flujo $flujo con un vendedor logueado. Esto no es permitido';


@Component({
  selector: 'app-sales',
  templateUrl: './sales.component.html'
})
export class SalesComponent implements OnInit {
  loading: boolean = true;
  salesWFOptions = SalesWorkflowSteps;
  salesWFCodes = SalesWorkflow;
  requestId: string;
  path: string | undefined;
  categoryCode: string | undefined;
  flowCode: string;
  startToken: string
  currentUrl: string | undefined;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private titleService: Title,
              // TODO mejorar
              public salesWorkflowService: SalesWorkflowService,
              private requestService: RequestService,
              private localStorageService: LocalStorageService,
              private customMessageService: CustomMessageService,
              private authService: AuthService,
              private utilService: UtilService,
              private pathNavigationService: PathNavigationService,
              private macroFlowService: MacroFlowService) {
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.categoryCode = this.route.snapshot.params['category_code'];
    this.requestId = this.route.snapshot.params['request_id'];
    this.path = this.route.snapshot.params['path'];
    this.startToken = this.route.snapshot.queryParams['start_token'];

    if(this.flowCode === undefined) {
      this.customMessageService.page404({ message: `Codigo del flujo de ventas no encontrado: ${this.flowCode}`});
    }
  }

  private shouldPreserveQueryParams(targetPath?: string): boolean {
    const currentQueryParams = this.route.snapshot.queryParams;

    // Parámetros que siempre deben preservarse (críticos para el sistema)
    const criticalParams = ['start_token', 'token', 'external_site', 'external_callback_url'];
    const hasCriticalParams = criticalParams.some(param => currentQueryParams[param]);

    // Parámetros específicos de verificación KYC
    const verificationParams = ['session_id', 'type'];
    const hasVerificationParams = verificationParams.some(param => currentQueryParams[param]);

    // Si hay parámetros críticos, siempre preservar
    if (hasCriticalParams) {
      return true;
    }

    // Si hay parámetros de verificación, solo preservar en el contexto de verificación
    if (hasVerificationParams) {
      const currentPath = this.route.snapshot.params['path'];
      const isCurrentlyInVerification = currentPath === 'verificacion-cliente';
      const isGoingToVerification = targetPath === 'verificacion-cliente';

      return isCurrentlyInVerification || isGoingToVerification;
    }

    // Si no hay parámetros especiales, no preservar
    return false;
  }

  async ngOnInit() {
    if (!this.validateFlowAuthentication(this.flowCode)) {
      return;
    }

    this.route.params.subscribe((params: any) => {
      const nextFlowCode = params['flow_code'];
      const flowChanged = nextFlowCode !== undefined && nextFlowCode !== this.flowCode;

      this.flowCode = nextFlowCode ?? this.flowCode;
      this.categoryCode = params['category_code'];
      this.requestId = params['request_id'];
      this.path = params['path'];

      const path = params['path'] ? this.capitalizeFirstLetter(params['path']) : 'Krediya'
      const pageTitle = `Krediya | ${path}`
      this.titleService.setTitle(pageTitle);

      if (flowChanged) {
        this.validateFlowAuthentication(this.flowCode);
      }
    })

    this.utilService.enablePageLoading();
    if(this.requestId === undefined) {
      this.getToken()
    }else{
      const ok = await this.getInfoRequest();
      if (!ok) return;
      this.sessionValidations();
    }
  }

  validateFlowAuthentication(flowCode: string): boolean {
    if (!Object.prototype.hasOwnProperty.call(SalesWorkflowRequiresAuthentication, flowCode)) {
      return true;
    }

    const requiresAuthentication = SalesWorkflowRequiresAuthentication[flowCode as SalesWorkflow];
    const isAuthenticated = this.authService.isAuthenticated();

    if (requiresAuthentication === isAuthenticated) {
      return true;
    }

    this.salesWorkflowService.currentStep = undefined!;
    this.utilService.disablePageLoading();

    const errorMessage = FLOW_SELLER_NOT_ALLOWED_MSG.replace('$flujo', `"${flowCode}"`);
    const errorPath = this.salesWorkflowService.getAuthenticationErrorPath(flowCode);

    this.customMessageService.responseNoticeHandler(
      {error: errorMessage},
      OriginMessageError.FRONTEND,
      {
        title: 'Acción no permitida',
        message: 'Estás tratando de hacer un flujo de una manera incorrecta',
        optionalMessage: 'Por favor, presiona el botón "Volver al inicio" para comenzar nuevamente',
        button: {label: 'Volver al inicio', routerLink: errorPath?.routerLink}
      }
    );

    return false;
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    history.forward();
  }

  getToken() {
    // TODO revisar, se puede mejorar
    const data = this.buildTokenData();

    this.authService.getToken(data).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok && resp.body?.data?.sales_token) {
          this.localStorageService.set(LocalStorageKey.salesToken, resp.body.data.sales_token)
          this.sessionValidations();
        }else{
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
    })
  }

  private buildTokenData(): { store_id: string; source: string; current_token: string } {

    let data: { store_id: string, source: string, current_token: string};
    if(this.startToken !== undefined) {
      if(!this.localStorageService.get(LocalStorageKey.token)) {
        this.localStorageService.clear()
      }
      this.localStorageService.set(LocalStorageKey.startToken, JSON.parse(atob(this.startToken)));
      data = {...JSON.parse(atob(this.startToken)), current_token: this.localStorageService.get(LocalStorageKey.salesToken)};
    }else if(this.localStorageService.get(LocalStorageKey.startToken)) {
      data = {...this.localStorageService.get(LocalStorageKey.startToken), current_token: this.localStorageService.get(LocalStorageKey.salesToken)};
    }else{
      data = { store_id: '', source: '', current_token: this.localStorageService.get(LocalStorageKey.salesToken)};
    }
    return data;

  }

  capitalizeFirstLetter(text: string) {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  sessionValidations() {
    if(this.categoryCode !== undefined) {
      if(this.requestService.categoryId === undefined) {
        this.getCategoryId();
      }else{
        this.loadWorkFlowList();
      }
    }else{
      this.loadWorkFlowList();
    }
  }

  getCategoryId() {
    this.requestService.getCategories().subscribe({
      next: (resp: any)=> {
        if(resp.status === HttpStatusCode.Ok) {
          let category = resp.body.data.list.filter((row: any) => row.code == this.categoryCode);
          if(category.length == 1) {
            this.requestService.categoryId = category[0].id;
            this.loadWorkFlowList();
          }else{
            this.customMessageService.page404({ ...resp, message: PageNotFoundReasons[PageNotFoundReasonCode.CATEGORY_CODE]});
            this.utilService.disablePageLoading();
          }
        }else{
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
    });
  }

  loadWorkFlowList() {

    if (this.salesWorkflowService.workflow !== undefined && this.salesWorkflowService.workflow.code == this.flowCode) {
      this.getSteps(this.salesWorkflowService.workflow.id)
      return;
    }

    this.salesWorkflowService.getList().subscribe({
      next: (resp: any)=> {
        if(resp.status === HttpStatusCode.Ok) {

          const workflow = this.salesWorkflowService.workflows.find((row: any) => row.code == this.flowCode);

          if (!workflow) {
            this.customMessageService.page404({...resp, message: PageNotFoundReasons[PageNotFoundReasonCode.FLOW_CODE]});
            this.utilService.disablePageLoading();
            return;
          }

          this.salesWorkflowService.workflow = workflow;

          this.getSteps(this.salesWorkflowService.workflow.id)
        }else{
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }

      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
    })
  }

  getSteps(flowId: string, isNextFlow: boolean = false) {
    // Necesario el codigo para que no se carguen los pasos de otro flujo
    if(Array.isArray(this.salesWorkflowService.steps) &&
      this.salesWorkflowService.steps.length > 0 &&
      this.salesWorkflowService.workflow.id === flowId) {
        this.loadStep();
        return;
    }

    this.salesWorkflowService.getStepsByFlowId(flowId, this.requestService.categoryId).subscribe({
      next: (resp: any)=> {
        if(resp.status === HttpStatusCode.Ok) {
          if(this.salesWorkflowService.steps.length > 0) {
            if(isNextFlow) {
              let nextStep = this.salesWorkflowService.getStepOfStepsByPosition(0);
              if(nextStep != null) this.updateCurrentStep(nextStep);
            }else{
              this.loadStep();
            }
          }else{
            this.customMessageService.page404({...resp, message: PageNotFoundReasons[PageNotFoundReasonCode.CATEGORY_OR_FLOW_CODE]});
            this.utilService.disablePageLoading();
          }
        }else{
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
    })
  }

  async loadStep(): Promise<void> {

    let step = await this.getStepCurrent();
    if (!step) {
      this.customMessageService.page404({message: PageNotFoundReasons[PageNotFoundReasonCode.PATH]});
      this.utilService.disablePageLoading();
      return;
    }
    let isUpdateCategoryStepId = this.getIsUpdateCategoryStepId(step);
    if(!isUpdateCategoryStepId) {
      this.localStorageService.set(LocalStorageKey.updateCategoryStepId, step.category_step_id);
      this.loadStepByUrl();
      return;
    }

    const { urlSecutiry, production } = environment;

    if (production || urlSecutiry) {
      this.requestId ? this.loadStepByRequest() : this.loadStepByUrl();
      return;
    }

    this.loadStepByUrl();
  }

  private getIsUpdateCategoryStepId(step: WorkflowStep): boolean {
    return step.setting !== null &&
      step.setting !== undefined &&
      step.setting.update_category_step_id !== undefined
        ? step.setting.update_category_step_id.value
        : true;
  }

  loadStepByRequest() {
    if(this.requestService.request !== undefined) {
      if (this.requestService.request['current_category_step_id'] != null) {
        const currentCategoryStepId = this.requestService.request['current_category_step_id'];
        this.searchStepByCategoryStepId(currentCategoryStepId, (dataStep) => {
          if (dataStep) {
            this.handleStep(dataStep);
          } else {
            this.customMessageService.responseNoticeHandler({ message: 'No se encontro ningun step', data: dataStep}, OriginMessageError.FRONTEND);
            }
          });
      } else {
        this.onNextStep()
      }
    }else{
      this.customMessageService.page404({message: PageNotFoundReasons[PageNotFoundReasonCode.REQUEST_ID]});
      this.utilService.disablePageLoading();
    }
  }


  private searchStepByCategoryStepId(
    currentCategoryStepId: string,
    callback: (dataStep: any | undefined) => void
  ): void {

    this.requestService.getInformationByCategoryStepId(currentCategoryStepId).subscribe({
      next: (resp: any) => {

        if (resp.status === HttpStatusCode.Ok) {

          const categoryStep = resp.body.data;

          const updatedCategoryStep = {
            ...categoryStep,
            category_step_id: categoryStep.id
          };

          const responseData = {
            step: updatedCategoryStep,
            flowCode: updatedCategoryStep.flow.code,
            categoryId: categoryStep.category?.id // Corregido el nombre de la propiedad
          };

          callback(responseData);
          return;

        } else {

          callback(undefined);
          return;

        }
      },
      error: (error: ResponseHttpError) => {
        callback(undefined);
        return;
      }
    });
  }

  private handleStep(dataStep: {
    step: any;
    flowCode: string;
    categoryId: string;
  }): void {

    if(dataStep === undefined) {
      this.utilService.disablePageLoading();
      return
    }

    let {step, flowCode, categoryId} = dataStep;

    if (!this.validateFlowAuthentication(flowCode)) {
      return;
    }

    if(!this.validateCategoryStep(step, flowCode, categoryId) ){
      return
    }

    let categoryCode = step.category?.code ?? undefined;
    this.flowCode = flowCode;
    this.categoryCode = categoryCode;
    this.requestService.categoryId = categoryId;

    const urlStep = this.salesWorkflowService.getUrlStepByCode(step.code);
    const navigationOptions = this.shouldPreserveQueryParams(urlStep.path)
      ? { queryParams: this.route.snapshot.queryParams }
      : {};

    this.router.navigate(
      [
        this.salesWorkflowService.buildUrl({
          flowCode: this.flowCode,
          categoryCode: this.categoryCode,
          requestId: this.requestId,
          path: urlStep.path,
        }),
      ],
      navigationOptions
    );
    setTimeout(() => this.salesWorkflowService.currentStep = step, 0);

  }

  validateCategoryStep(step: any, flowCode: string, categoryId: string): boolean {

    if (flowCode !== this.flowCode && flowCode === this.macroFlowService.getAutogestionTiendaFlow()) {
      const matchedCategory = step.category.id === categoryId ? step.category : undefined;
      this.categoryCode = matchedCategory?.code;

      const instructionsPath = this.salesWorkflowService.getUrlStepByCode(SalesWorkflowSteps['final-instructions-client']).path;

      const targetUrl = this.salesWorkflowService.buildUrl({
        flowCode: this.macroFlowService.getAutogestionClienteFlow(),
        categoryCode: this.categoryCode,
        requestId: this.requestId,
        path: instructionsPath
      });

      this.salesWorkflowService.currentStep = { code: SalesWorkflowSteps['final-instructions-client'] } as WorkflowStep;
      this.salesWorkflowService.steps = []

      // Para este caso especial, no preservar query params ya que es un flujo diferente
      this.router.navigate([targetUrl]);
      setTimeout(() => (this.utilService.disablePageLoading()), 0);
      return false;
    }

    return true;

  }

  loadStepByUrl() {
    let step: WorkflowStep;
    if(this.path === undefined) {
      step = this.salesWorkflowService.steps[0];
      this.path = this.salesWorkflowService.getUrlStepByCode(step.code).path
    }else{
      step = this.salesWorkflowService.getStepByName(this.path);
      if (!step || Object.keys(step).length === 0) {
        this.customMessageService.page404({message: PageNotFoundReasons[PageNotFoundReasonCode.PATH]});
        this.utilService.disablePageLoading();
        return;
      }
    }
    const navigationOptions = this.shouldPreserveQueryParams(this.path)
      ? { queryParams: this.route.snapshot.queryParams }
      : {};

    this.router.navigate([this.salesWorkflowService.buildUrl({flowCode: this.flowCode, categoryCode: this.categoryCode, requestId: this.requestId, path: this.path})],
      navigationOptions);
    setTimeout(() => this.salesWorkflowService.currentStep = step, 0)
  }

  updateCurrentStep(step: WorkflowStep): void {

    const urlStep = this.salesWorkflowService.getUrlStepByCode(step.code);

    let isUpdateCategoryStepId = this.getIsUpdateCategoryStepId(step);

    const navigateToStep = () => {
      if (urlStep) {
        const navigationOptions = this.shouldPreserveQueryParams(urlStep.path)
          ? { queryParams: this.route.snapshot.queryParams }
          : {};

        this.router.navigate(
          [this.salesWorkflowService.buildUrl({flowCode: this.flowCode, categoryCode: this.categoryCode, requestId: this.requestId, path: urlStep.path,})],
          navigationOptions
        );
        this.salesWorkflowService.currentStep = step
      }
    };

    if (!this.requestId) {
      navigateToStep();
      return;
    }

    if (!isUpdateCategoryStepId) {
      navigateToStep();
      return;
    }

    const data = { category_step_id: step.category_step_id };

    this.requestService.update(this.requestId, data).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.NoContent) {
          navigateToStep();
        }else {
          this.customMessageService.responseNoticeHandler(resp);
        }
        this.loading = false;
      },
      error: (error: ResponseHttpError) => {
        if ( error.status === HttpStatusCode.Locked) {
          this.handleRequestLocked(error as any);
        }else{
          this.customMessageService.responseNoticeHandler(error);
          this.loading = false;
        }
      },
    });
  }

  async onNextStep() {

    if(this.requestId != undefined) {
      const ok = await this.getInfoRequest();
      if (!ok) return;
    }

    const currentStep = await this.getStepCurrent();
    if (!currentStep || Object.keys(currentStep).length === 0) {
      this.customMessageService.page404({message: PageNotFoundReasons[PageNotFoundReasonCode.PATH]});
      this.utilService.disablePageLoading();
      return;
    }
    let index = this.salesWorkflowService.steps.findIndex((row:WorkflowStep) => row.category_step_id == currentStep.category_step_id);
    let nextStep = this.salesWorkflowService.steps[index + 1] ?? this.salesWorkflowService.steps.at(-1);

    if(this.localStorageService.get(LocalStorageKey.updateCategoryStepId)) {
      this.localStorageService.remove(LocalStorageKey.updateCategoryStepId)
      this.loadStepByRequest();
    }else {
      this.updateCurrentStep(nextStep)
    }
  }

  async onNextStepWithData(data: {requestId: string, categoryCode: string, queryParams: any}) {

    const nextFlowCode = data.categoryCode !== undefined && this.flowCode !== SalesWorkflow['domicilio-vendedor']
      ? this.macroFlowService.getNextFlowInMacro(this.flowCode)
      : undefined;

    if (nextFlowCode && !this.validateFlowAuthentication(nextFlowCode)) {
      return;
    }

    if(this.requestId != undefined) {
      const ok = await this.getInfoRequest();
      if (!ok) return;
    }

    if(data.requestId !== undefined) {
      this.requestId = data.requestId;
    }
    if(data.queryParams !== undefined) {
      this.route.snapshot.queryParams = data.queryParams;
    }
    if(data.categoryCode !== undefined && this.flowCode !== SalesWorkflow['domicilio-vendedor']) {
      if (!nextFlowCode) {
        return;
      }

      this.categoryCode = data.categoryCode;
      this.flowCode = this.macroFlowService.getNextFlowInMacro(this.flowCode) ?? '';
      this.path = undefined;
      let workFlow = this.salesWorkflowService.workflows.filter((row: any) => row.code == this.flowCode)[0]
      this.getSteps(workFlow.id, true)
    }else{
      this.onNextStep()
    }
  }

  async onGotoStep(event: { flowCode: string; stepCode: string; categoryId?: string; categoryCode?: string; requestId?: string }): Promise<void> {
    const requestedFlowCode = event.flowCode === undefined ? this.flowCode : event.flowCode;
    if (!this.validateFlowAuthentication(requestedFlowCode)) {
      return;
    }

    if (event.requestId) {
      this.requestId = event.requestId;
    }

    if(this.requestId != undefined) {
      const ok = await this.getInfoRequest();
      if (!ok) return;
    }

    let { flowCode, stepCode, categoryId } = event;
    let currentFlowCode: string = flowCode === undefined ? this.flowCode : flowCode;
    categoryId = categoryId === undefined ? this.requestService.categoryId : categoryId;


    while (currentFlowCode) {
      const workflow = this.salesWorkflowService.workflows.find((row: any) => row.code === currentFlowCode);
      if (!workflow) break;

      try {
        if (stepCode === SalesWorkflowSteps['select-category']) {
          categoryId = undefined;
          this.categoryCode = undefined;
        }
        const resp: any = await firstValueFrom(this.salesWorkflowService.getListStepsByFlowId(workflow.id, categoryId));
        if (resp.status === HttpStatusCode.Ok) {
          const steps: WorkflowStep[] = resp.body.data.list;
          const targetStep = steps.find((row: WorkflowStep) => row.code === stepCode);

          if (targetStep) {
            if (!this.validateFlowAuthentication(currentFlowCode)) {
              return;
            }

            this.flowCode = currentFlowCode;
            this.salesWorkflowService.steps = steps;
            this.updateCurrentStep(targetStep);
            return;
          }
        }
      } catch (error: any) {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
        return;
      }

      const previousFlowCode: string | undefined = this.macroFlowService.getPreviousFlowInMacro(currentFlowCode);
      if (!previousFlowCode || previousFlowCode === currentFlowCode) {
        break;
      }
      currentFlowCode = previousFlowCode;
    }

    this.customMessageService.page404({ message: PageNotFoundReasons[PageNotFoundReasonCode.PATH] });
    this.utilService.disablePageLoading();
  }

  async getStepCurrent(): Promise<WorkflowStep | undefined> {
    let step = await this.searchStepInLocalList();

    if (Object.keys(<WorkflowStep>step).length > 0) {
      return step;
    } else {
      // Si no se encuentra el paso, intentamos actualizar la lista desde el backend una vez
      const workflowId = this.salesWorkflowService.workflow?.id;
      if (workflowId) {
        try {
          await firstValueFrom(this.salesWorkflowService.getStepsByFlowId(workflowId, this.requestService.categoryId));
          step = await this.searchStepInLocalList();
        } catch (error) {
          console.error('Error al intentar refrescar los pasos desde getStepCurrent:', error);
        }
      }

      return step;
    }
  }

  private async searchStepInLocalList(): Promise<WorkflowStep | undefined> {
    const pathCurrent = this.route.snapshot.params['path'];
    const hasQueryParams = Object.keys(this.route.snapshot.queryParams).length > 0;

    const stepClientVerification = this.salesWorkflowService.getUrlStepByCode(SalesWorkflowSteps['client-verification'] as string)?.path;
    const stepClientIdentification = this.salesWorkflowService.getUrlStepByCode(SalesWorkflowSteps['client-identification'] as string)?.path;

    if (pathCurrent === stepClientVerification && !hasQueryParams) {
      // Espera 10 segundos antes de continuar, para darle chance el renderizado al KYC
      await new Promise(resolve => setTimeout(resolve, 10000));
    }

    if (pathCurrent === stepClientIdentification) {
      return this.salesWorkflowService.getStepByName(pathCurrent as string);
    }

    const { urlSecutiry } = environment;

    if (urlSecutiry && this.requestId) {
      const infoRequest = this.requestService.request;
      const categoryStepId = infoRequest?.['current_category_step_id'];

      if (categoryStepId) {
        const step = this.salesWorkflowService.steps.find(
          (s: WorkflowStep) => s.category_step_id === categoryStepId
        );
        if (step) return step;
      }
    }

    return this.salesWorkflowService.getStepByName(pathCurrent as string);
  }

  async getInfoRequest(): Promise<boolean> {
    try {
      const resp: any = await firstValueFrom(this.requestService.get(this.requestId));
      if (resp.status === HttpStatusCode.Ok && resp.body.data != null) {
        this.requestService.request = resp.body.data;
        return true;
      } else {
        this.customMessageService.responseNoticeHandler(resp);
        return false;
      }
    } catch (error: any) {
      if ( error.status === HttpStatusCode.Locked) {
        this.handleRequestLocked(error as any);
        return false;
      }else{
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
      return false;
    }
  }

  private handleRequestLocked(resp: any): void {
    const messageError = resp.error?.message ?? 'Solicitud no disponible';
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotAvailable(this.flowCode, state);
  }

  protected readonly SalesWorkflow = SalesWorkflow;
}
